import { Paper, Title, Text, TextInput, PasswordInput, Button, Flex, Stepper, SimpleGrid, Box } from "@mantine/core";
import { IconBuilding, IconMail, IconLock, IconBriefcase } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VerifyEmailModal from "../signup/VerifyModal";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import { SignupFlowLayout } from "./SignupFlowLayout";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

function SignupPage() {
  const navigate = useNavigate();

  const [emailModalOpened, setEmailModalOpened] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = Cookies.get("token");

    if (!token) return;

    try {
      const decoded = jwtDecode<{ role: string; exp: number }>(token);

      if (decoded.exp > Date.now() / 1000) {
        if (decoded.role === "ADMIN") {
          navigate("/admin/companies", { replace: true });
        } else {
          navigate("/profile", { replace: true });
        }
      } else {
        Cookies.remove("token");
      }
    } catch {
      Cookies.remove("token");
    }
  }, []);

  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    designation: "",
    email: "",
    phoneNumber: "",
    phoneCountryCode: "",
    password: "",
    confirmPassword: "",
  });

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = formData.password.length >= 8;
  const doPasswordsMatch = formData.confirmPassword === "" || formData.password === formData.confirmPassword;

  const isStep1Valid =
    formData.name.trim() !== "" &&
    formData.companyName.trim() !== "" &&
    formData.designation.trim() !== "" &&
    formData.phoneNumber.trim() !== "" &&
    isValidEmail(formData.email) &&
    isPasswordValid &&
    formData.password === formData.confirmPassword;

  const handleStep1Submit = async () => {
    if (!isStep1Valid) return;

    setLoading(true);
    try {
      await trpc.auth.registerSignupStep1.mutate({
        email: formData.email,
        password: formData.password,
        userName: formData.name,
        companyName: formData.companyName,
        userDesignation: formData.designation,
        userPhoneNumber: `${formData.phoneCountryCode}${formData.phoneNumber}`,
      });

      successNotification({
        title: "Check your email",
        message: "We sent a verification link. After verifying, you can complete your company profile.",
      });

      navigate(`/resend-email?email=${encodeURIComponent(formData.email)}`);
      setEmailModalOpened(true);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleModalContinue = () => {
    setEmailModalOpened(false);
    navigate("/registration-success", {
      state: {
        companyName: formData.companyName,
        location: "—",
        email: formData.email,
      },
    });
  };

  return (
    <SignupFlowLayout>
      <Paper p="xl" radius="lg" maw={980} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
        <Stepper active={0} mb="xl">
          <Stepper.Step label="Account" description="Verify email" />
          <Stepper.Step label="Company details" />
        </Stepper>

        <Title order={2} mb="md" c="slate.7" style={{ borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
          Create your account
        </Title>
        <Text fz="lg" c="slate.6" mb="lg">
          Personal Details
        </Text>

        <TextInput
          label="Contact Person Name"
          placeholder="Enter name"
          required
          size="md"
          mb="md"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
        />

        <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
          <TextInput
            label="Company Name"
            placeholder="Enter your company name"
            required
            size="md"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.currentTarget.value })}
            leftSection={<IconBuilding size={18} />}
          />
          <TextInput
            label="Designation"
            placeholder="Enter designation"
            size="md"
            value={formData.designation}
            onChange={(e) => setFormData({ ...formData, designation: e.currentTarget.value })}
            leftSection={<IconBriefcase size={18} />}
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
          <TextInput
            label="Company Email Address"
            required
            type="email"
            placeholder="Enter email"
            size="md"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.currentTarget.value })}
            leftSection={<IconMail size={18} />}
            error={formData.email && !isValidEmail(formData.email) ? "Please enter a valid email address" : null}
          />
          <Box>
            <Text fz="sm" fw={500} mb={3}>
              Phone Number{" "}
              <Text span c="red">
                *
              </Text>
            </Text>
            <PhoneInput
              defaultCountry="in"
              value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
              onChange={(phone, meta) => {
                const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                setFormData((prev) => ({ ...prev, phoneCountryCode: countryCode, phoneNumber: nationalNumber }));
              }}
              style={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                border: "1px solid var(--mantine-color-gray-3)",
                borderLeft: "none",
                borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                outline: "none",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  height: "36px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                  padding: "0 12px",
                  backgroundColor: "white",
                },
                dropdownStyleProps: {
                  style: {
                    backgroundColor: "white",
                    boxShadow: "0 4px 12px #00000026",
                    border: "1px solid var(--mantine-color-gray-3)",
                    zIndex: 1000,
                  },
                },
              }}
            />
          </Box>
        </SimpleGrid>

        <Flex direction={{ base: "column", sm: "row" }} gap="md" mb="xl">
          <PasswordInput
            style={{ flex: 1 }}
            label="Password"
            placeholder="Enter password"
            required
            size="md"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.currentTarget.value })}
            visible={showPassword}
            onVisibilityChange={setShowPassword}
            leftSection={<IconLock size={18} />}
            error={formData.password.length > 0 && !isPasswordValid ? "Password must be at least 8 characters" : null}
          />

          <PasswordInput
            style={{ flex: 1 }}
            label="Confirm Password"
            placeholder="Re-enter password"
            required
            size="md"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.currentTarget.value })}
            visible={showConfirmPassword}
            onVisibilityChange={setShowConfirmPassword}
            leftSection={<IconLock size={18} />}
            error={formData.confirmPassword && !doPasswordsMatch ? "Passwords don't match" : null}
          />
        </Flex>

        <Flex justify="flex-end">
          <Button size="lg" px="xl" onClick={handleStep1Submit} loading={loading} disabled={!isStep1Valid || loading}>
            Continue
          </Button>
        </Flex>
      </Paper>

      <VerifyEmailModal
        opened={emailModalOpened}
        onClose={() => setEmailModalOpened(false)}
        email={formData.email}
        onContinue={handleModalContinue}
      />
    </SignupFlowLayout>
  );
}

export default SignupPage;
